import { Settings } from "lucide-react";
import { useState } from "react";

const GoalForm = ({ goal, parentGoalId, parentGoal, onSave, onCancel }) => {
  const [formData, setFormData] = useState(goal || {
    name: '',
    description: '',
    target: 10,
    unit: 'times',
    timeframe: 'weekly',
    resetFrequency: 'never',
    resetTarget: null,
    goalDirection: 'increase',
    scalePercent: 5,
    scaleUpEnabled: true,
    scaleDownEnabled: true,
    roundUp: true,
    minTarget: 1,
    maxTarget: 100,
    progress: 0,
    currentResetProgress: 0,
    resetsCompleted: 0,
  });

  // Get available reset frequencies based on timeframe
  const getAvailableResetFrequencies = (timeframe) => {
    const options = [{ value: 'never', label: 'Never (accumulate over entire period)' }];
    
    if (timeframe === 'daily') {
      // Daily timeframe doesn't need reset options
      return options;
    }
    
    if (timeframe === 'weekly') {
      options.push({ value: 'daily', label: 'Daily (reset each day)' });
    }
    
    if (timeframe === 'monthly') {
      options.push({ value: 'daily', label: 'Daily (reset each day)' });
      options.push({ value: 'weekly', label: 'Weekly (reset each week)' });
    }
    
    if (timeframe === 'annually') {
      options.push({ value: 'daily', label: 'Daily (reset each day)' });
      options.push({ value: 'weekly', label: 'Weekly (reset each week)' });
      options.push({ value: 'monthly', label: 'Monthly (reset each month)' });
    }
    
    return options;
  };

  const availableResets = getAvailableResetFrequencies(formData.timeframe);

  // Helper to get period count based on timeframe and reset frequency
  const getMaxResetPeriods = (timeframe, resetFrequency) => {
    if (resetFrequency === 'never') return null;
    
    if (timeframe === 'weekly' && resetFrequency === 'daily') return 7;
    if (timeframe === 'monthly' && resetFrequency === 'daily') return 31;
    if (timeframe === 'monthly' && resetFrequency === 'weekly') return 4;
    if (timeframe === 'annually' && resetFrequency === 'daily') return 365;
    if (timeframe === 'annually' && resetFrequency === 'weekly') return 52;
    if (timeframe === 'annually' && resetFrequency === 'monthly') return 12;
    
    return null;
  };

  const maxResetPeriods = getMaxResetPeriods(formData.timeframe, formData.resetFrequency);

  // Update resetFrequency when timeframe changes
  const handleTimeframeChange = (newTimeframe) => {
    const newAvailable = getAvailableResetFrequencies(newTimeframe);
    const currentIsValid = newAvailable.some(opt => opt.value === formData.resetFrequency);
    
    setFormData({
      ...formData,
      timeframe: newTimeframe,
      resetFrequency: currentIsValid ? formData.resetFrequency : 'never',
      resetTarget: currentIsValid && formData.resetFrequency !== 'never' ? formData.resetTarget : null
    });
  };

  const handleSubmit = () => {
    if (!formData.name || formData.target <= 0) {
      alert('Please enter a goal name and target');
      return;
    }
    
    // Validate reset fields
    if (formData.resetFrequency !== 'never') {
      if (!formData.resetTarget || formData.resetTarget <= 0) {
        alert(`Please set a ${formData.resetFrequency} target`);
        return;
      }
    }
    
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        {goal ? 'Edit Goal' : parentGoalId ? 'Create Subgoal' : 'Create New Goal'}
      </h2>
      {parentGoal && (
        <p className="text-sm text-indigo-600 mb-4">
          Parent Goal: <span className="font-semibold">{parentGoal.name}</span>
        </p>
      )}
      
      <div className="space-y-4">
        {/* Basic Information */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Goal Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="e.g., Weekly Workouts"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Optional details about your goal"
            rows="2"
          />
        </div>

        {/* Timeframe - When auto-scaling occurs */}
        <div className="border-t pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timeframe (when goal auto-scales)
          </label>
          <select
            value={formData.timeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="daily">Daily (auto-scales every day)</option>
            <option value="weekly">Weekly (auto-scales every week)</option>
            <option value="monthly">Monthly (auto-scales every month)</option>
            <option value="annually">Annually (auto-scales every year)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            The period after which your goal will auto-scale based on achievement
          </p>
        </div>

        {/* Reset Frequency - How often progress resets */}
        {formData.timeframe !== 'daily' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Progress Reset Frequency
            </label>
            <select
              value={formData.resetFrequency}
              onChange={(e) => setFormData({ 
                ...formData, 
                resetFrequency: e.target.value,
                resetTarget: e.target.value !== 'never' ? (formData.resetTarget || 10) : null,
                target: e.target.value !== 'never' ? (maxResetPeriods || formData.target) : formData.target
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {availableResets.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            
            <div className={`mt-2 p-3 rounded-lg ${
              formData.resetFrequency === 'never' ? 'bg-green-50' : 'bg-blue-50'
            }`}>
              <p className="text-xs text-gray-700">
                {formData.resetFrequency === 'never' 
                  ? `✓ Progress accumulates throughout the ${formData.timeframe} period. Example: "Complete 3 workouts per ${formData.timeframe}" - you can do them anytime during the period.`
                  : `✓ Progress resets every ${formData.resetFrequency}. Example: "Read 30 minutes ${formData.resetFrequency}, 5 ${formData.resetFrequency} periods per ${formData.timeframe}" - you must meet the target each ${formData.resetFrequency}.`}
              </p>
            </div>
          </div>
        )}

        {/* Goal Direction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Goal Direction
          </label>
          <select
            value={formData.goalDirection}
            onChange={(e) => setFormData({ ...formData, goalDirection: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="increase">Increase (do more over time)</option>
            <option value="decrease">Decrease (do less over time)</option>
          </select>
        </div>

        {/* Target Configuration */}
        <div className="border-t pt-4">
          {formData.resetFrequency === 'never' ? (
            // SIMPLE: Just one target for the entire period
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target (per {formData.timeframe})
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Amount</label>
                  <input
                    type="number"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., 3"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Unit</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., times"
                  />
                </div>
              </div>
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Your Goal:</strong> "Complete <strong>{formData.target || '___'} {formData.unit || 'units'}</strong> per {formData.timeframe}"
                </p>
              </div>
            </>
          ) : (
            // COMPLEX: Reset target + period count
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.resetFrequency.charAt(0).toUpperCase() + formData.resetFrequency.slice(1)} Target
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Amount per {formData.resetFrequency}</label>
                      <input
                        type="number"
                        value={formData.resetTarget || ''}
                        onChange={(e) => setFormData({ ...formData, resetTarget: parseInt(e.target.value) || null })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., 30"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Unit</label>
                      <input
                        type="text"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., minutes"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.resetFrequency.charAt(0).toUpperCase() + formData.resetFrequency.slice(1)} Periods Required (per {formData.timeframe})
                  </label>
                  <input
                    type="number"
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., 5"
                    min="1"
                    max={maxResetPeriods}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many {formData.resetFrequency} periods you need to meet the target
                    {maxResetPeriods && ` (max: ${maxResetPeriods} for ${formData.timeframe})`}
                  </p>
                </div>
              </div>
              
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Your Goal:</strong> "Complete <strong>{formData.resetTarget || '___'} {formData.unit || 'units'}</strong> per {formData.resetFrequency}, on <strong>{formData.target || '___'}</strong> {formData.resetFrequency} periods per {formData.timeframe}"
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Progress resets every {formData.resetFrequency}. You need to meet or exceed {formData.resetTarget || '___'} {formData.unit || 'units'} on {formData.target || '___'} different {formData.resetFrequency} periods.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Auto-Scaling Settings */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Auto-Scaling Settings
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scale Percentage
              </label>
              <input
                type="number"
                value={formData.scalePercent}
                onChange={(e) => setFormData({ ...formData, scalePercent: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="0.1"
                step="0.1"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.scaleUpEnabled}
                  onChange={(e) => setFormData({ ...formData, scaleUpEnabled: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-sm text-gray-700">Enable scale up</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.scaleDownEnabled}
                  onChange={(e) => setFormData({ ...formData, scaleDownEnabled: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-sm text-gray-700">Enable scale down</span>
              </label>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.roundUp}
                  onChange={(e) => setFormData({ ...formData, roundUp: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="text-sm text-gray-700">Round up (unchecked = round down)</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Target</label>
                <input
                  type="number"
                  value={formData.minTarget}
                  onChange={(e) => setFormData({ ...formData, minTarget: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Target</label>
                <input
                  type="number"
                  value={formData.maxTarget}
                  onChange={(e) => setFormData({ ...formData, maxTarget: parseInt(e.target.value) || 100 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  min="1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            {goal ? 'Update Goal' : 'Create Goal'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoalForm;